import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import React, { ChangeEvent } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { formatCreditCardNumber, formatCVV } from "./form_components_utils";
import { SSText } from "../SSText";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { IosSwitch } from "../IosSwitch";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import styles from "./stripe.module.css";
import { InstrumentSansFont } from "src/components/reusable/context/AppThemeProvider/AppThemeProvider";

export function InputError(props: { children: React.ReactNode }) {
  const theme = useTheme();

  return (
    <Box
      className="error-message"
      fontSize={"0.8rem"}
      lineHeight={1.4}
      color={theme.palette.error.main}
      fontWeight={500}
    >
      {props.children}
    </Box>
  );
}

export const AptTextField = (props: { name?: string }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props?.name || "appUnit";

  const errorMessage = errors[fieldName]?.message as string;

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName)}
        fullWidth
        placeholder={"Apt Unit"}
        size="small"
      />
      {errorMessage ? <InputError>{errorMessage}</InputError> : null}
    </Stack>
  );
};

// US ZIP (75001, 75001-1234) or Canadian postal code (R3C 4A5, R3C4A5)
export const ZIP_CODE_PATTERN = {
  value: /^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$/,
  message: "Enter a valid ZIP or postal code (e.g. 75001 or R3C 4A5)",
};

export const ZipCodeTextField = (props: {
  name?: string;
  pattern?: { value: RegExp; message: string };
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "zipCode";

  const errorMessage = errors[fieldName]?.message as string;

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, {
          required: "Zip Code is Required",
          ...(props.pattern ? { pattern: props.pattern } : {}),
        })}
        fullWidth
        placeholder={"Zip Code"}
        size="small"
      />
      {errorMessage ? <InputError>{errorMessage}</InputError> : null}
    </Stack>
  );
};

export const FirstNameTextField = (props: {
  name?: string;
  defaultValue?: string;
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "firstName";

  const errorMessage = errors[fieldName]?.message as string;

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, {
          required: "First Name is Required",
        })}
        fullWidth
        placeholder={"First Name"}
        size="small"
        defaultValue={props?.defaultValue || ""}
      />
      {errorMessage ? <InputError>{errorMessage}</InputError> : null}
    </Stack>
  );
};

export const LastNameTextField = (props: {
  name?: string;
  defaultValue?: string;
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "lastName";

  const errorMessage = errors[fieldName]?.message as string;

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, {
          required: "Last Name is Required",
        })}
        fullWidth
        placeholder={"Last Name"}
        size="small"
        defaultValue={props?.defaultValue || ""}
      />
      {errorMessage ? <InputError>{errorMessage}</InputError> : null}
    </Stack>
  );
};

export const CardNumberTextField = (props: { name?: string }) => {
  const {
    register,
    setValue,
    trigger,
    formState: { errors, submitCount },
  } = useFormContext();
  const fieldName = props.name || "cardNumber";

  const errorMessage = errors[fieldName]?.message as string;

  const handleCardInput = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const formatted = formatCreditCardNumber(value);
    setValue("cardNumber", formatted);
    submitCount > 0 && trigger("cardNumber");
  };

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, {
          required: "Card Number is Required",
          minLength: {
            value: 17,
            message: "Please enter 15/16 digit card number.",
          },
          maxLength: {
            value: 20,
            message: "Please enter 15/16 digit card number.",
          },
        })}
        fullWidth
        onChange={handleCardInput}
        placeholder={"Card Number"}
        size="small"
      />
      {errorMessage ? <InputError>{errorMessage}</InputError> : null}
    </Stack>
  );
};

export const CardCvcTextField = (props: { name?: string }) => {
  const {
    register,
    setValue,
    trigger,
    formState: { errors, submitCount },
  } = useFormContext();
  const fieldName = props.name || "cardCvc";

  const errorMessage = errors[fieldName]?.message as string;

  const handleCardCVV = (event: ChangeEvent<HTMLInputElement>) => {
    const value = formatCVV(event.target.value);
    setValue(fieldName, value);
    submitCount > 0 && trigger("cvv");
  };

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, {
          required: "CVV is Required",
          minLength: { value: 3, message: "Please enter 3/4 digit cvv" },
          maxLength: { value: 4, message: "Please enter 3/4 digit cvv" },
        })}
        fullWidth
        placeholder={"CVV"}
        size="small"
        onChange={handleCardCVV}
      />
      {errorMessage ? <InputError>{errorMessage}</InputError> : null}
    </Stack>
  );
};

export const TermsCheckbox = (props: {
  name?: string;
  label: string | React.ReactNode;
}) => {
  const theme = useSSTheme();
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "terms";
  const errorMessage = errors[fieldName]?.message as string;

  return (
    <>
      <FormControlLabel
        sx={{
          color: theme.colors.gray[200],
          alignItems: "flex-start",
          py: 0.5,
          transition: "all 0.3s ease",
          margin: 0,

          "&:hover": {
            backgroundColor: theme.colors.gray[50],
            borderRadius: "4px",
          },
        }}
        label={
          <SSText variant="caption" fontSize={"0.9rem"}>
            {props.label}
          </SSText>
        }
        control={
          <Checkbox
            // disableRipple
            size="medium"
            disableRipple
            sx={{
              "& .MuiSvgIcon-root": {
                fontSize: "1.8rem",
                marginTop: "-8px",
                marginLeft: "-6px",
              },
            }}
            {...register("terms", {
              required: "Please accept the terms and conditions",
            })}
          />
        }
      />
      {errorMessage ? <InputError>{errorMessage}</InputError> : null}
    </>
  );
};

export function SaveCardSwitch(props: { name?: string; label: string }) {
  const { control } = useFormContext();
  const fieldName = props.name || "saveCard";

  return (
    <Stack direction={"row"} alignItems={"center"} spacing={2}>
      <Controller
        name={fieldName}
        control={control}
        defaultValue={false}
        render={({ field }) => {
          return (
            <IosSwitch
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          );
        }}
      />

      <SSText variant="h6" fontWeight={"600"} color="text.primary">
        {props.label}
      </SSText>
    </Stack>
  );
}

interface IReasons {
  name?: string;
  required?: boolean;
  disabled: boolean;
}

export const OtherReason = ({
  name = "otherReason",
  required = false,
  disabled,
}: IReasons) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <Box py={1}>
      <TextField
        multiline
        maxRows={4}
        rows={4}
        disabled={disabled}
        {...register(name, { required })}
        fullWidth
        placeholder={"Please specify"}
        label={"Please specify"}
        size="small"
      />
      <SSText color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {errors[name]?.type === "required" && (
          <span>Other reason is required</span>
        )}
      </SSText>
    </Box>
  );
};

export function StripeCardNumberInput(props: {
  name?: string;
  placeholder?: string;
}) {
  const { colors } = useSSTheme();
  const { control, register } = useFormContext();
  const [isFocused, setIsFocused] = React.useState(false);
  const fieldName = props?.name || "cardNumber";

  return (
    <Stack flexGrow={1}>
      <Stack
        sx={{
          height: 40,
          width: "100%",
          border: "1px solid",
          borderRadius: "4px",
          outline: isFocused ? `1px solid ${colors.teamPrimary}` : "unset",
          borderColor: isFocused ? "unset" : "rgba(0, 0, 0, 0.23)",
          ":hover": {
            cursor: "text",
            borderColor: isFocused ? colors.teamPrimary : colors.common.black,
          },
        }}
      >
        <Controller
          {...register(fieldName)}
          name={fieldName}
          control={control}
          render={({ field }) => {
            return (
              <CardNumberElement
                onChange={field.onChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                options={{
                  placeholder: props?.placeholder || "",
                  style: {
                    base: {
                      fontSize: "1.15rem",
                      color: colors.common.black,
                      fontWeight: 200,
                      fontFamily: [
                        `${InstrumentSansFont.style.fontFamily}`,
                        "-apple-system",
                        "Inter",
                        "Helvetica",
                        "Arial",
                        "sans-serif",
                      ].join(","),
                      "::placeholder": {
                        color: "rgba(0, 0, 0, 0.23)",
                      },
                    },
                  },
                }}
                className={styles.stripeInput}
              />
            );
          }}
        />
      </Stack>
    </Stack>
  );
}

export function StripeCardExipryInput(props: { placeholder?: string }) {
  const { colors } = useSSTheme();
  const [isFocused, setIsFocused] = React.useState(false);

  return (
    <Stack flexGrow={1}>
      <Stack
        sx={{
          height: 40,
          width: "100%",
          border: "1px solid",
          borderRadius: "4px",
          outline: isFocused ? `1px solid ${colors.teamPrimary}` : "unset",
          borderColor: isFocused ? "unset" : "rgba(0, 0, 0, 0.23)",
          ":hover": {
            cursor: "text",
            borderColor: isFocused ? colors.teamPrimary : colors.common.black,
          },
        }}
      >
        <CardExpiryElement
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          options={{
            placeholder: props?.placeholder || "",
            style: {
              base: {
                fontSize: "1.15rem",
                color: colors.common.black,
                fontWeight: 200,
                fontFamily: [
                  `${InstrumentSansFont.style.fontFamily}`,
                  "-apple-system",
                  "Inter",
                  "Helvetica",
                  "Arial",
                  "sans-serif",
                ].join(","),
                "::placeholder": {
                  color: "rgba(0, 0, 0, 0.23)",
                },
              },
            },
          }}
          className={styles.stripeInput}
        />
      </Stack>
    </Stack>
  );
}

export function StripeCardCvcInput(props: { placeholder?: string }) {
  const { colors } = useSSTheme();
  const [isFocused, setIsFocused] = React.useState(false);

  return (
    <Stack flexGrow={1}>
      <Stack
        sx={{
          height: 40,
          width: "100%",
          border: "1px solid",
          borderRadius: "4px",
          outline: isFocused ? `1px solid ${colors.teamPrimary}` : "unset",
          borderColor: isFocused ? "unset" : "rgba(0, 0, 0, 0.23)",
          ":hover": {
            cursor: "text",
            borderColor: isFocused ? colors.teamPrimary : colors.common.black,
          },
        }}
      >
        <CardCvcElement
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          options={{
            placeholder: props?.placeholder || "CVV",
            style: {
              base: {
                fontSize: "1.15rem",
                color: colors.common.black,
                fontWeight: 200,
                fontFamily: [
                  `${InstrumentSansFont.style.fontFamily}`,
                  "-apple-system",
                  "Inter",
                  "Helvetica",
                  "Arial",
                  "sans-serif",
                ].join(","),
                "::placeholder": {
                  color: "rgba(0, 0, 0, 0.23)",
                },
              },
            },
          }}
          className={styles.stripeInput}
        />
      </Stack>
    </Stack>
  );
}
